import { useMemo } from 'react'

import { useMasterSummaryGetApi } from '@/common/api/modules/site'
import { MasterSummaryItem } from '@/common/api/params/site'
import {
  MASTER_GROUP_ORDER,
  masterTypeGroup,
  masterTypeName,
  masterTypeOrder
} from '@/common/app/modules/master'
import { useSiteId } from '@/common/app/site'
import { dateTimeStr } from '@/common/form/date'
import { MasterType } from '@/common/model/master/w2Master'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import DetailPageLoader from '@/components/container/DetailPageLoader'
import AppTitleBadge from '@/components/container/layout/AppTitleBadge'

// MasterType から W2 関連マスタを抽出（種別追加時の定義漏れを防ぐため、ここでハードコードしない）
const W2_ALL_TYPES = Object.values(MasterType).filter((t) => t.startsWith('w2_'))

const SOURCE_LABELS: Record<string, string> = {
  manual: '手動取込',
  ftp: 'FTP',
  http: 'API/タグ',
  auto: '自動'
}

const sourceToBadgeType = (source: string): BadgeType => {
  switch (source) {
    case 'ftp':
      return BadgeType.EMPHASIS
    case 'manual':
      return BadgeType.CAUTION
    case 'http':
      return BadgeType.SUCCESS
    default:
      return BadgeType.SECONDARY
  }
}

type MasterRow = MasterSummaryItem & { label: string; order: number }

const MasterSummaryPage = () => {
  const siteId = useSiteId()
  const { data, error } = useMasterSummaryGetApi({ site_id: siteId })

  const masters = useMemo<MasterRow[]>(() => {
    if (!data) return []
    const apiMasters = data.masters || []
    const apiMap: Record<string, MasterSummaryItem> = {}
    apiMasters.forEach((m) => {
      apiMap[m.master_type] = m
    })

    // W2マスタは0件でも行として並べる。受注など W2 以外の種別は API の返り値から拾う
    const extraTypes = apiMasters.map((m) => m.master_type).filter((t) => !t.startsWith('w2_'))
    const allTypes = [...W2_ALL_TYPES, ...extraTypes]

    return allTypes.map((mt) => {
      const base = apiMap[mt] || {
        master_type: mt,
        count: 0,
        active_count: null,
        last_updated_at: null,
        last_upload_at: null,
        upload_source: null
      }
      return {
        ...base,
        master_type: mt,
        label: masterTypeName(mt as MasterType),
        order: masterTypeOrder(mt as MasterType)
      } as MasterRow
    })
  }, [data])

  const totalRecords = useMemo(() => masters.reduce((sum, m) => sum + m.count, 0), [masters])

  const groups = useMemo(() => {
    const g: Record<string, MasterRow[]> = {}
    masters.forEach((m) => {
      const group = masterTypeGroup(m.master_type as MasterType)
      if (!g[group]) g[group] = []
      g[group].push(m)
    })
    Object.values(g).forEach((items) => items.sort((a, b) => a.order - b.order))
    return g
  }, [masters])

  const orderedGroups = MASTER_GROUP_ORDER.filter((g) => groups[g])

  return (
    <DetailPageLoader error={error} data={data}>
      {data && (
        <Main type={MainLayoutType.FULL}>
          <PageTitle
            title='マスター状況サマリー'
            description='連携済みマスタデータの件数・取込状況を確認できます'
            badgeNode={<AppTitleBadge type='master' />}
          />
          <Content>
            <Area noBorder>
              <SummaryItemList>
                <SummaryItem>
                  <Text weight='bold'>マスタ種別</Text>
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {masters.length}
                    </Text>
                    <Text size={14}>種別</Text>
                  </ItemAlign>
                </SummaryItem>
                <SummaryItem>
                  <Text weight='bold'>総レコード数</Text>
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {totalRecords.toLocaleString()}
                    </Text>
                    <Text size={14}>件</Text>
                  </ItemAlign>
                </SummaryItem>
                <SummaryItem>
                  <Text weight='bold'>データ連動</Text>
                  <Text weight='bold' size={16}>
                    W2Commerce完全連動
                  </Text>
                </SummaryItem>
                <SummaryItem>
                  <Text weight='bold'>最終取込</Text>
                  <Text weight='bold' size={16}>
                    {data.latest_upload ? dateTimeStr(data.latest_upload) : '-'}
                  </Text>
                </SummaryItem>
              </SummaryItemList>
            </Area>

            {orderedGroups.map((groupName) => (
              <div key={groupName}>
                <SummaryHeading>{groupName}</SummaryHeading>
                <Area tableWrap noBorder>
                  <Table>
                    <thead>
                      <TableRow head>
                        <TableHeadItem>
                          <Text>マスタ</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text>登録件数</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text>有効件数</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text>最新取込日</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text>取込手段</Text>
                        </TableHeadItem>
                      </TableRow>
                    </thead>
                    <tbody>
                      {groups[groupName].map((m) => (
                        <TableRow key={m.master_type}>
                          <TableRowItem>
                            <ItemAlign gapX={4}>
                              <Text weight='bold'>{m.label}</Text>
                              <Text size={10}>({m.master_type})</Text>
                            </ItemAlign>
                          </TableRowItem>
                          <TableRowItem width={188}>
                            <Text weight='bold' color={m.count === 0 ? 'disabled' : 'primary'}>
                              {m.count.toLocaleString()}
                            </Text>
                          </TableRowItem>
                          <TableRowItem width={188}>
                            {m.active_count != null ? (
                              <ItemAlign gapX={4}>
                                <Text
                                  weight='bold'
                                  color={m.active_count === m.count ? 'success' : 'warning'}
                                >
                                  {m.active_count.toLocaleString()}
                                </Text>
                                {m.active_count < m.count && (
                                  <Text size={10}>
                                    ({Math.round((m.active_count / m.count) * 100)}%)
                                  </Text>
                                )}
                              </ItemAlign>
                            ) : (
                              <Text color='success'>{m.count.toLocaleString()}</Text>
                            )}
                          </TableRowItem>
                          <TableRowItem width={188}>
                            <Text
                              size={12}
                              color={m.last_upload_at ? 'primary' : TextColor.SECONDARY}
                            >
                              {m.last_upload_at ? dateTimeStr(m.last_upload_at) : '-'}
                            </Text>
                          </TableRowItem>
                          <TableRowItem width={188}>
                            {m.upload_source ? (
                              <Badge badgeType={sourceToBadgeType(m.upload_source)}>
                                {SOURCE_LABELS[m.upload_source] ?? m.upload_source}
                              </Badge>
                            ) : (
                              <Text color={TextColor.SECONDARY}>-</Text>
                            )}
                          </TableRowItem>
                        </TableRow>
                      ))}
                    </tbody>
                  </Table>
                </Area>
              </div>
            ))}
          </Content>
        </Main>
      )}
    </DetailPageLoader>
  )
}

export default MasterSummaryPage
